import React, { useState, useEffect } from 'react'
import * as Icons from 'lucide-react'
import {
  Tag,
  Edit,
  Trash2,
  Plus,
  X,
  Sparkles,
  AlertTriangle,
} from 'lucide-react'
import { VENDOR_TYPES } from '../utils/mockData'
import { adminCategoriesService } from '../services'
import './Categories.css'

const Categories = () => {
  const [categories, setCategories] = useState([])
  const [loading, setLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [selectedCategory, setSelectedCategory] = useState(null)

  const loadCategories = () => {
    console.log('Loading categories...')
    adminCategoriesService.list().then((list) => {
      console.log('Categories loaded:', list)
      setCategories(list || [])
      setLoading(false)
    }).catch((err) => {
      console.error('Failed to load categories:', err)
      setLoading(false)
    })
  }

  useEffect(() => {
    loadCategories()
  }, [])

  const handleAddCategory = () => {
    setSelectedCategory(null)
    setShowModal(true)
  }

  const handleEditCategory = (category) => {
    setSelectedCategory(category)
    setShowModal(true)
  }

  const handleDeleteCategory = async (categoryId) => {
    if (!window.confirm('Are you sure you want to delete this category?')) return
    try {
      await adminCategoriesService.delete(categoryId)
      setCategories((prev) => prev.filter((c) => c.id !== categoryId))
    } catch (e) {
      console.error(e)
    }
  }

  return (
    <div className="categories-page">
      <div className="page-header">
        <div className="header-titles">
          <h1>Categories</h1>
          <p>Organize vendors and plans into searchable categories.</p>
        </div>
      </div>

      <div className="page-actions">
        <div style={{ flex: 1 }} />
        <button className="btn-primary" onClick={handleAddCategory}>
          <Plus size={18} />
          Add Category
        </button>
      </div>

      <div className="category-cards">
        {loading ? (
          <p style={{ padding: '2rem' }}>Loading categories...</p>
        ) : (
          categories.map((category) => (
            <CategoryCard
              key={category.id}
              category={category}
              onEdit={() => handleEditCategory(category)}
              onDelete={() => handleDeleteCategory(category.id)}
            />
          ))
        )}
      </div>

      {showModal && (
        <CategoryEditorModal
          category={selectedCategory}
          onClose={() => setShowModal(false)}
          onSave={async (categoryData) => {
            try {
              if (selectedCategory) {
                await adminCategoriesService.update(selectedCategory.id, { name: categoryData.name, status: categoryData.status })
                setCategories((prev) =>
                  prev.map((c) => (c.id === selectedCategory.id ? { ...c, ...categoryData } : c))
                )
              } else {
                const created = await adminCategoriesService.create({ name: categoryData.name, status: categoryData.status || 'active' })
                setCategories((prev) => [...prev, { ...created, plansCount: 0 }])
              }
              setShowModal(false)
            } catch (e) {
              console.error(e)
            }
          }}
        />
      )}
    </div>
  )
}

// Available icons for category selection
const AVAILABLE_ICONS = [
  'Heart', 'Activity', 'Users', 'Gem', 'Coffee', 'Utensils', 'Trophy',
  'Star', 'Music', 'Camera', 'Map', 'Sun', 'Moon', 'Gamepad2', 'Wine',
  'Smile', 'Tent', 'Dumbbell', 'Ticket', 'ShoppingBag', 'SoccerBall', 'Football', 'TennisBall'
]

console.log('Lucide Icons available:', Icons)

// Helper to render dynamic icon
const DynamicIcon = ({ name, size = 24, className = '' }) => {
  const IconComponent = Icons[name] || Icons.Tag // Fallback to Tag icon
  return <IconComponent size={size} className={className} />
}

// Category Card Component
const CategoryCard = ({ category, onEdit, onDelete }) => {
  if (!category) return null;

  return (
    <div className="category-card" onClick={onEdit}>
      <div className="category-card-header">
        <div className="category-icon">
          <DynamicIcon name={category.icon || 'Tag'} size={24} />
        </div>
        <div className="category-actions" onClick={(e) => e.stopPropagation()}>
          <button className="action-btn" onClick={onEdit} title="Edit">
            <Edit size={16} />
          </button>
          <button className="action-btn danger" onClick={onDelete} title="Delete">
            <Trash2 size={16} />
          </button>
        </div>
      </div>

      <h3 className="category-name">{category.name || 'Untitled Category'}</h3>

      <div className="category-stats" style={{ borderTop: 'none', paddingTop: '0.5rem' }}>
        <div className="category-plans-count">
          <strong>{category.plansCount ?? 0}</strong> plans using this category
        </div>
      </div>
    </div>
  )
}

// Category Editor Modal
const CategoryEditorModal = ({ category, onClose, onSave }) => {
  const [name, setName] = useState(category?.name || '')
  const [selectedIcon, setSelectedIcon] = useState(category?.icon || 'Tag')
  const [showIconSelector, setShowIconSelector] = useState(false)

  const handleSubmit = (e) => {
    e.preventDefault()
    if (!name.trim()) return

    // Send the name, selected icon, keeping status active by default.
    onSave({ ...category, name: name.trim(), icon: selectedIcon, status: category?.status || 'active' })
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal category-modal" style={{ maxWidth: '450px' }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">{category ? 'Edit Category' : 'Create Category'}</h2>
          <button className="modal-close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label">Category Name *</label>
              <input
                type="text"
                className="form-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Food, Sport, Padel"
                required
                autoFocus
              />
            </div>

            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label">Category Icon</label>
              <div className="icon-selector-container" style={{ position: 'relative' }}>
                <button
                  type="button"
                  className="icon-selector-trigger"
                  onClick={() => setShowIconSelector(!showIconSelector)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    width: '100%',
                    padding: '0.625rem 0.875rem',
                    border: '1px solid var(--border-color)',
                    borderRadius: '0.5rem',
                    background: 'var(--background)',
                    color: 'var(--text-primary)',
                    cursor: 'pointer',
                    textAlign: 'left'
                  }}
                >
                  <DynamicIcon name={selectedIcon} size={20} />
                  <span>Choose an icon...</span>
                </button>

                {showIconSelector && (
                  <div className="icon-dropdown" style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    marginTop: '0.5rem',
                    background: 'var(--surface)',
                    border: '1px solid var(--border-color)',
                    borderRadius: '0.5rem',
                    padding: '1rem',
                    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
                    zIndex: 10,
                    display: 'grid',
                    gridTemplateColumns: 'repeat(5, 1fr)',
                    gap: '0.5rem',
                    maxHeight: '200px',
                    overflowY: 'auto'
                  }}>
                    {AVAILABLE_ICONS.map(iconName => (
                      <button
                        key={iconName}
                        type="button"
                        onClick={() => {
                          setSelectedIcon(iconName)
                          setShowIconSelector(false)
                        }}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          padding: '0.5rem',
                          border: 'none',
                          borderRadius: '0.375rem',
                          background: selectedIcon === iconName ? 'var(--primary-color)' : 'transparent',
                          color: selectedIcon === iconName ? 'white' : 'var(--text-secondary)',
                          cursor: 'pointer',
                          transition: 'all 0.2s'
                        }}
                        title={iconName}
                      >
                        <DynamicIcon name={iconName} size={20} />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={!name.trim()}>
              {category ? 'Save Changes' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default Categories
